import {
  formatNumber,
  formatPercent,
  successReportSchema,
  SUCCESS_WINDOWS,
  type SuccessMetrics,
  type SuccessWindow,
} from '@fluxus/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  CalendarCheck,
  FileSpreadsheet,
  Gauge,
  ListChecks,
  Siren,
  UserRound,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { useSearchParams } from 'react-router';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const WINDOW_LABELS: Record<SuccessWindow, string> = { 30: '30 dias', 90: '90 dias', 365: '12 meses' };
const brDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const plural = (n: number, one: string, many: string) => `${formatNumber(n)} ${n === 1 ? one : many}`;
/** Dias com uma casa: "0,5 dia", "1,5 dia", "2,0 dias" (plural a partir de 2). */
const days = (n: number | null, suffix = '') =>
  n === null ? '—' : `${formatNumber(n, 1)} ${n >= 2 ? 'dias' : 'dia'}${suffix}`;

type Tile = { key: string; icon: LucideIcon; label: string; value: string; detail: string };

/** Os números de cada métrica (os mesmos no consolidado e por empresa). */
function tilesOf(m: SuccessMetrics): Tile[] {
  return [
    {
      key: 'kpis',
      icon: Gauge,
      label: 'KPIs atualizados no prazo',
      value: formatPercent(m.kpisOnTime.rate),
      detail: `${formatNumber(m.kpisOnTime.hit)} de ${plural(m.kpisOnTime.total, 'período cobrado', 'períodos cobrados')}`,
    },
    {
      key: 'actions',
      icon: ListChecks,
      label: 'Ações concluídas no prazo',
      value: formatPercent(m.actionsOnTime.rate),
      detail: `${formatNumber(m.actionsOnTime.hit)} de ${plural(m.actionsOnTime.total, 'ação vencida', 'ações vencidas')}`,
    },
    {
      key: 'deviation',
      icon: Siren,
      label: 'Do desvio à ação (média)',
      value: days(m.deviationToAction.averageDays),
      detail: `${formatNumber(m.deviationToAction.withAction)} de ${plural(m.deviationToAction.deviations, 'desvio', 'desvios')} com plano`,
    },
    {
      key: 'users',
      icon: UsersRound,
      label: 'Usuários que acessaram',
      value: formatNumber(m.users.accessed),
      detail: `de ${plural(m.users.active, 'conta ativa', 'contas ativas')}`,
    },
    {
      key: 'executives',
      icon: UserRound,
      label: 'Acesso de executivos',
      value: days(m.executives.accessDaysPerWeek, ' por semana'),
      detail: `${formatNumber(m.executives.accessed)} de ${plural(m.executives.active, 'executivo acessou', 'executivos acessaram')}`,
    },
    {
      key: 'analysis',
      icon: CalendarCheck,
      label: 'Projetos com análise crítica',
      value: formatPercent(m.criticalAnalysis.rate),
      detail: `${formatNumber(m.criticalAnalysis.hit)} de ${plural(m.criticalAnalysis.total, 'projeto em andamento', 'projetos em andamento')}`,
    },
    {
      key: 'spreadsheet',
      icon: FileSpreadsheet,
      label: 'Lançado direto no sistema',
      value: formatPercent(m.spreadsheet.rate),
      detail: `${plural(m.spreadsheet.direct, 'digitado', 'digitados')} · ${plural(m.spreadsheet.imported, 'importado de planilha', 'importados de planilha')} · janela anterior: ${formatPercent(m.spreadsheet.previousRate)}`,
    },
  ];
}

/** Recorte de todos os números (DEC-071). */
const SCOPE_NOTE =
  'Entram as empresas clientes ativas que você acompanha (Admin Fluxus: todas; Consultor: as alocadas a você) e, nelas, os projetos em andamento. A janela vai dos últimos 30 dias, 90 dias ou 12 meses até hoje. Sem base para a conta, o número aparece como "—".';

/** Como cada número é calculado (DEC-071). */
const DEFINITIONS: [string, string][] = [
  [
    'KPIs atualizados no prazo',
    'Dos períodos de KPI cujo prazo de lançamento (fim do período mais a tolerância dos alertas da empresa) venceu na janela antes de hoje e depois do cadastro do KPI: a parte cujo primeiro resultado foi lançado até o prazo. O prazo que vence hoje ainda pode ser cumprido; KPIs arquivados, calculados, fora da vigência e projetos em diagnóstico ficam de fora.',
  ],
  [
    'Ações concluídas no prazo',
    'Das ações de planos não arquivados, sem as canceladas, com prazo na janela antes de hoje: a parte concluída até o prazo. A ação que vence hoje ainda pode ser concluída.',
  ],
  [
    'Do desvio à ação',
    'Dos desvios abertos na janela (KPI abaixo da meta, queda seguida, objetivo em risco e desvio informado): quantos ganharam plano de ação e, desses, a média de dias até o primeiro plano criado a partir do desvio. Os desvios sem plano ficam fora da média.',
  ],
  [
    'Clientes usando',
    'Das empresas clientes ativas, as que tiveram acesso de alguém ativo da empresa na janela.',
  ],
  [
    'Usuários ativos por cliente',
    'Média, por empresa cliente ativa, das pessoas que acessaram a plataforma na janela.',
  ],
  [
    'Usuários que acessaram',
    'Das pessoas ativas da empresa, as que entraram ou usaram a plataforma na janela.',
  ],
  [
    'Acesso de executivos',
    'Das pessoas ativas com o perfil Diretor / CEO hoje: quantas acessaram na janela e a média de dias com acesso por semana por executivo ativo (quem não acessou entra com zero).',
  ],
  [
    'Projetos com análise crítica',
    'Dos projetos em andamento: a parte com pelo menos uma análise crítica encerrada (registrada) na janela.',
  ],
  [
    'Lançado direto no sistema',
    'Dos valores de resultado lançados na janela nos KPIs ativos dos projetos em andamento: a parte digitada na plataforma, contra a importada de planilha (Central de Importação), e a mesma parte na janela anterior, de mesmo tamanho, para ver a redução das planilhas paralelas. Conta a versão que lança ou muda o valor; não contam a que só anexa evidência, a remoção nem os resultados calculados pelo sistema.',
  ],
];

function Tiles({ tiles, compact }: { tiles: Tile[]; compact?: boolean }) {
  return (
    <ul className={cn('grid gap-3 lg:grid-cols-4', compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2')}>
      {tiles.map((t) => (
        <li
          key={t.key}
          className={cn(
            'flex min-w-0 flex-col gap-1 break-words rounded-lg border border-border bg-surface',
            compact ? 'p-3 sm:p-4' : 'p-4',
          )}
        >
          <t.icon aria-hidden className="size-4 text-muted-foreground" />
          <span className={cn('font-extrabold tabular-nums', compact ? 'text-base sm:text-lg' : 'text-2xl')}>
            {t.value}
          </span>
          <span className="text-sm font-medium">{t.label}</span>
          <span className="text-xs text-muted-foreground">{t.detail}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Indicadores de sucesso do produto (T-205b, RF-51, PRD §31; DEC-071): o consolidado das empresas que a pessoa da
 * Fluxus acessa e cada empresa, na janela escolhida (30 dias, 90 dias ou 12 meses).
 */
export function IndicadoresSucessoPage() {
  const { client } = useAuth();
  const [params, setParams] = useSearchParams();
  const asked = Number(params.get('dias'));
  const windowDays: SuccessWindow = (SUCCESS_WINDOWS as readonly number[]).includes(asked)
    ? (asked as SuccessWindow)
    : 30;
  const report = useQuery({
    queryKey: ['success-metrics', windowDays],
    queryFn: ({ signal }) =>
      client.request('/success-metrics', {
        query: { days: windowDays },
        schema: successReportSchema,
        signal,
      }),
  });
  const choose = (d: SuccessWindow) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (d === 30) out.delete('dias');
        else out.set('dias', String(d));
        return out;
      },
      { replace: true },
    );

  return (
    <PageContainer>
      <PageHeader
        title="Indicadores de sucesso"
        description="Como as empresas clientes estão usando a plataforma: prazos de KPIs e ações, resposta aos desvios, acesso, análises críticas e o uso no lugar das planilhas."
        actions={
          <div role="group" aria-label="Período" className="flex rounded-md border border-border p-0.5">
            {SUCCESS_WINDOWS.map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={d === windowDays}
                onClick={() => choose(d)}
                className={cn(
                  'rounded px-3 py-1.5 text-sm font-medium',
                  d === windowDays
                    ? 'bg-brand text-brand-foreground'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {WINDOW_LABELS[d]}
              </button>
            ))}
          </div>
        }
      />
      <div className="mt-6 flex flex-col gap-8">
        {report.isPending ? (
          <LoadingState rows={4} label="Calculando os indicadores" />
        ) : report.isError ? (
          <ErrorState
            error={report.error}
            onRetry={() => void report.refetch()}
            retrying={report.isFetching}
          />
        ) : report.data.companies.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="Nenhuma empresa cliente ativa"
            description="Os indicadores aparecem para as empresas clientes ativas que você acompanha na Central de Empresas."
          />
        ) : (
          <>
            <section aria-label="Consolidado" className="flex flex-col gap-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-lg font-bold">Consolidado</h2>
                <p className="text-sm text-muted-foreground">
                  De {brDate(report.data.window.from)} a {brDate(report.data.window.to)}
                </p>
              </div>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <li className="flex flex-col gap-1 rounded-lg border border-border bg-surface p-4">
                  <Building2 aria-hidden className="size-4 text-muted-foreground" />
                  <span className="text-2xl font-extrabold tabular-nums">
                    {formatNumber(report.data.consolidated.clients.using)} de{' '}
                    {formatNumber(report.data.consolidated.clients.active)}
                  </span>
                  <span className="text-sm font-medium">Clientes usando</span>
                  <span className="text-xs text-muted-foreground">empresas ativas com acesso na janela</span>
                </li>
                <li className="flex flex-col gap-1 rounded-lg border border-border bg-surface p-4">
                  <UsersRound aria-hidden className="size-4 text-muted-foreground" />
                  <span className="text-2xl font-extrabold tabular-nums">
                    {report.data.consolidated.activeUsersPerClient === null
                      ? '—'
                      : formatNumber(report.data.consolidated.activeUsersPerClient, 1)}
                  </span>
                  <span className="text-sm font-medium">Usuários ativos por cliente</span>
                  <span className="text-xs text-muted-foreground">
                    média de quem acessou, por empresa ativa
                  </span>
                </li>
              </ul>
              <Tiles tiles={tilesOf(report.data.consolidated)} />
            </section>

            <section aria-label="Por empresa" className="flex flex-col gap-3">
              <h2 className="text-lg font-bold">Por empresa</h2>
              <ul className="flex flex-col gap-4">
                {report.data.companies.map((c) => (
                  <li key={c.organization.id}>
                    <article
                      aria-label={c.organization.name}
                      className="rounded-lg border border-border bg-surface-raised p-4"
                    >
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-bold">{c.organization.name}</h3>
                        <Badge variant={c.using ? 'brand' : 'neutral'}>
                          {c.using ? 'Usando' : 'Sem acesso na janela'}
                        </Badge>
                      </div>
                      <Tiles tiles={tilesOf(c.metrics)} compact />
                    </article>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}

        <details className="rounded-lg border border-border bg-surface p-4 text-sm">
          <summary className="cursor-pointer font-semibold">Como cada número é calculado</summary>
          <p className="mt-3 text-muted-foreground">{SCOPE_NOTE}</p>
          <dl className="mt-3 flex flex-col gap-2">
            {DEFINITIONS.map(([term, text]) => (
              <div key={term}>
                <dt className="font-medium">{term}</dt>
                <dd className="text-muted-foreground">{text}</dd>
              </div>
            ))}
          </dl>
        </details>
      </div>
    </PageContainer>
  );
}
